/**
 * Primitive Design Tokens
 *
 * These are the foundational values for the design system.
 * They are abstract and platform-agnostic, meant to be referenced by semantic tokens.
 */

// Color Primitives - Warm palette (orange/pink tones)
export const colorWarm = {
  100: '#FFE5D9',
  200: '#FFCDB2',
  300: '#FFB4A2',
  400: '#FF9E8F',
  500: '#FD9543', // Primary warm
  600: '#F996CD', // Warm pink
  700: '#FF7B5A', // Warm coral
  800: '#FB8FB0', // Light pink
  900: '#FFB08F', // Peach
} as const

// Color Primitives - Red palette (ADS - high energy) - spans burgundy to orange (Duolingo-inspired)
export const colorRed = {
  100: '#FFE5E5',
  200: '#FFCAB0',
  300: '#FFB3B3',
  400: '#FF9999',
  500: '#A61550', // Deep burgundy/wine
  600: '#D91C5C', // Bright burgundy
  700: '#FF5757', // Coral/salmon
  800: '#FF7B3D', // Coral-orange
  900: '#FF8500', // Vibrant orange
} as const

// Color Primitives - Blue palette (SEO - calm, focused) - spans cyan to indigo
export const colorBlue = {
  100: '#E3F2FD',
  200: '#BBDEFB',
  300: '#90CAF9',
  400: '#64B5F6',
  500: '#32B9F2', // Cyan blue (landing page)
  600: '#69DCFF', // Light cyan (landing page)
  700: '#0B99FF', // Bright blue
  800: '#1E88E5', // Deep blue
  900: '#5C6BC0', // Indigo
} as const

// Color Primitives - Green palette (growth, success) - spans lime to teal
export const colorGreen = {
  100: '#E8F5E9',
  200: '#C8E6C9',
  300: '#A5D6A7',
  400: '#81C784',
  500: '#84CC16', // Lime green
  600: '#10B981', // Emerald
  700: '#14B8A6', // Teal
  800: '#06B6D4', // Cyan-teal
  900: '#22D3EE', // Bright cyan
} as const

// Color Primitives - Purple palette (social media - creative, engaging) - spans light blue to violet
export const colorPurple = {
  100: '#F3E8FF',
  200: '#E9D5FF',
  300: '#D8B4FE',
  400: '#C084FC',
  500: '#69DCFF', // Light blue (social/connection)
  600: '#8BB4FF', // Periwinkle
  700: '#A78BFA', // Light purple
  800: '#9333EA', // Vibrant purple
  900: '#7C3AED', // Deep violet
} as const

// Neutral Colors
export const colorNeutral = {
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#FAFAFA',
  gray100: '#F5F5F5',
  gray200: '#E5E5E5',
  gray300: '#D4D4D4',
  gray400: '#A3A3A3',
  gray500: '#737373',
  gray600: '#525252',
  gray700: '#404040',
  gray800: '#262626',
  gray900: '#171717',
} as const

// Spacing Scale
export const spacing = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
} as const

// Border Radius
export const radius = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  '3xl': '32px',
  full: '9999px',
} as const

// Opacity Scale
export const opacity = {
  0: 0,
  10: 0.1,
  20: 0.2,
  30: 0.3,
  40: 0.4,
  50: 0.5,
  60: 0.6,
  65: 0.65,
  70: 0.7,
  75: 0.75,
  80: 0.8,
  90: 0.9,
  100: 1.0,
} as const

// Blob Sizes (for animated background)
export const blobSize = {
  sm: 280,
  md: 300,
  lg: 410,
  xl: 450,
  '2xl': 500,
} as const
